import { useState } from 'react';
import Select, { components } from 'react-select';
import { ReactComponent as DropdownIcon } from '../../../assets/pipeline/dropdown-small.svg';
import { STAGE_OPTIONS } from '../options';
import { groupHeadingStyle, placeholderStyle } from '../selectStyles';
import { reilmAPI } from '../../../services/apiClient';
import { getStatusLabel, resolveSelectOption } from '../utils';

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <DropdownIcon />
  </components.DropdownIndicator>
);

const formatOptionLabel = ({ label, stageLabel }, { context }) => {
  // stageLabel is absent on a synthetic fallback option (resolveSelectOption, on a legacy stored
  // value with no canonical match) -- the two-line layout below would show a blank bold line above
  // the raw value, so that case falls through to the single-line form used for menu rows instead.
  if (context === 'value' && stageLabel) {
    return (
      <div className="flex flex-col justify-center leading-none gap-y-[3px]">
        <span className="text-[11px] font-bold text-brand-dark tracking-wide">
          {stageLabel}
        </span>
        <span className="text-[13px] font-light text-brand-dark">
          {label}
        </span>
      </div>
    );
  }
  return <span className="text-[13px] text-brand-dark leading-none">{label}</span>;
};

// lineHeight: 'normal' overrides the inherited grid-cell line-height (CSS2.1 strut). The old
// bottom:12px offset was removed round 3, tuned against a track height that no longer applies. See README.md.
export const singleValueStyle = (base) => ({
  ...base,
  margin: 0,
  overflow: 'visible',
  lineHeight: 'normal',
});

// The actual fix for the shared strut: this is ValueContainer, react-select's own grid container
// whose track was sizing to its tallest occupant (input, missed by singleValueStyle's own fix). See README.md.
export const valueContainerStyle = (base) => ({
  ...base,
  padding: '0 10px',
  height: '100%',
  lineHeight: 'normal',
});

const StageCellEditor = ({ params, activeStage, onStageUpdate, onSaveOk, onSaveFailed }) => {
  const [saving, setSaving] = useState(false);

  const groupedOptions = (activeStage === 'all' ? STAGE_OPTIONS : STAGE_OPTIONS.filter(g => g.key === activeStage))
    .map(g => ({ ...g, options: g.options.map(o => ({ ...o, stageLabel: g.label })) }))
  // row.stage is the backend field name (property.stage). A value with no canonical match still
  // renders via resolveSelectOption's passthrough, not vanish into the placeholder.
  const currentOption = resolveSelectOption(groupedOptions, params.row.stage, getStatusLabel);

  const handleChange = async (selected) => {
    if (!selected || selected.value === params.row.stage) return;
    setSaving(true);
    try {
      await reilmAPI.updatedPropertiesDetails(params.row.id, { stage: selected.value });
      onStageUpdate(params.row.id, selected.value);
      onSaveOk?.(`stage:${params.row.id}`);
    } catch (err) {
      // Row stays unchanged (onStageUpdate above never ran) -- isDisabled={saving} below already
      // rules out a race on this control, so there is nothing to roll back, only the shared banner
      // (src/pages/README.md) to raise.
      onSaveFailed?.(`stage:${params.row.id}`, err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select
      value={currentOption}
      options={groupedOptions}
      onChange={handleChange}
      isDisabled={saving}
      formatOptionLabel={formatOptionLabel}
      components={{ DropdownIndicator, IndicatorSeparator: null }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      placeholder="Select"
      styles={{
        container: (base) => ({ ...base, width: '100%', right: '8px' }),
        control: (base, state) => ({
          ...base,
          minHeight: 52,
          height: 56,
          border: 'none',
          // border: state.isFocused ? '1px solid #4CBC18' : '1px solid #DAE4D5',
          borderRadius: 8,
          boxShadow: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          '&:hover': { borderColor: 'transparent' },
        }),
        // paddingBottom is this grid's own addition on top of the shared heading style, not carried
        // by PropertyDashboard's copy — the two menus sit at different densities.
        groupHeading: (base) => ({ ...groupHeadingStyle(base), paddingBottom: 2 }),
        placeholder: placeholderStyle,
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? '#E7F0E2'
            : state.isFocused
            ? '#f8fef4'
            : '#fff',
          color: '#17270D',
          cursor: 'pointer',
          fontSize: 13,
          padding: '6px 12px',
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        input: (base) => ({ ...base, caretColor: 'transparent' }),
        valueContainer: valueContainerStyle,
        singleValue: singleValueStyle,
      }}
    />
  );
};

export default StageCellEditor;
